function Section({ title, children }) {
  return (
    <div className="py-3 border-b border-[#2e2e2e] last:border-0">
      <p className="text-xs text-[#555] uppercase tracking-wider mb-2">{title}</p>
      {children}
    </div>
  );
}

function Tags({ items, color = "emerald" }) {
  if (!items || items.length === 0)
    return <p className="text-sm text-[#555] italic">None detected</p>;
  const cls =
    color === "emerald"
      ? "bg-emerald-500/15 text-emerald-300"
      : color === "sky"
      ? "bg-sky-500/15 text-sky-300"
      : "bg-amber-500/15 text-amber-300";
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((t) => (
        <span key={t} className={`text-xs px-2.5 py-1 rounded-full ${cls}`}>
          {t}
        </span>
      ))}
    </div>
  );
}

function SentimentBadge({ sentiment }) {
  if (!sentiment) return null;
  const map = {
    positive: "bg-green-500/20 text-green-400",
    negative: "bg-red-500/20 text-red-400",
    neutral:  "bg-gray-500/20 text-gray-400",
  };
  const cls = map[sentiment.toLowerCase()] || map.neutral;
  return (
    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize ${cls}`}>
      {sentiment}
    </span>
  );
}

function ImageBlock({ img }) {
  return (
    <div className="bg-[#222] rounded-xl p-4 space-y-3">
      <p className="text-xs text-[#555] truncate">{img.path.split("/").pop()}</p>

      {img.error ? (
        <p className="text-sm text-red-400">{img.error}</p>
      ) : (
        <>
          {img.description && (
            <p className="text-sm text-gray-300 leading-relaxed">{img.description}</p>
          )}
          {img.mood && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#555]">Mood</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-300">
                {img.mood}
              </span>
            </div>
          )}
          <div>
            <p className="text-xs text-[#555] mb-1.5">Objects</p>
            <Tags items={img.objects} color="emerald" />
          </div>
          {img.detected_text && (
            <div>
              <p className="text-xs text-[#555] mb-1">Text detected</p>
              <p className="text-sm text-gray-300 italic">"{img.detected_text}"</p>
            </div>
          )}
          {img.brand_logos && img.brand_logos.length > 0 && (
            <div>
              <p className="text-xs text-[#555] mb-1.5">Brand logos</p>
              <Tags items={img.brand_logos} color="amber" />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function AnalysisCard({ analysis }) {
  if (!analysis) return null;

  const { images = [], videos = [], caption } = analysis;
  const hasContent = images.length > 0 || videos.length > 0 || caption;
  if (!hasContent) return null;

  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-2xl p-5 space-y-1">
      <h2 className="text-lg font-semibold mb-4">AI Analysis</h2>

      {/* Caption analysis */}
      {caption && !caption.error && (
        <>
          {caption.topic && (
            <Section title="Topic">
              <p className="text-sm text-gray-200">{caption.topic}</p>
            </Section>
          )}
          {caption.sentiment && (
            <Section title="Sentiment">
              <SentimentBadge sentiment={caption.sentiment} />
            </Section>
          )}
          {caption.themes && caption.themes.length > 0 && (
            <Section title="Themes">
              <Tags items={caption.themes} color="sky" />
            </Section>
          )}
          {caption.summary && (
            <Section title="Summary">
              <p className="text-sm text-gray-300 leading-relaxed">{caption.summary}</p>
            </Section>
          )}
        </>
      )}

      {/* Video summaries */}
      {videos.filter((v) => !v.error && v.summary).map((vid, i) => (
        <Section key={i} title={`Video ${i + 1} Summary`}>
          <p className="text-sm text-gray-300 leading-relaxed">{vid.summary}</p>
          <p className="text-xs text-[#555] mt-1">{vid.frames_used} frames analyzed</p>
        </Section>
      ))}

      {/* Per-image analysis */}
      {images.length > 0 && (
        <div>
          <p className="text-xs text-[#555] uppercase tracking-wider mb-3 pt-3">
            Image Analysis
          </p>
          <div className="space-y-3">
            {images.map((img, i) => (
              <ImageBlock key={i} img={img} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
